// TicketManagement: Admin manual creation modal, re-send, export, scan history, print
import React, { useEffect, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getUserRole } from '../../utils/auth';
import ReactCountryFlag from 'react-country-flag';
// Helper to get ISO country code from country name
function getCountryCode(countryName) {
  const map = {
    'Tanzania': 'TZ',
    'Kenya': 'KE',
    'Uganda': 'UG',
    'Rwanda': 'RW',
    'Burundi': 'BI',
    'South Africa': 'ZA',
    'Nigeria': 'NG',
    'Ghana': 'GH',
  };
  return map[countryName] || '';
}
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Grid, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PrintPreviewModal from '../../components/PrintPreviewModal';
import TicketDesignModal, { TicketPreview } from '../../components/TicketDesignModal';
import api from '../../services/api';

// Helper to pick a design: prefer exact category, otherwise the first available design
function pickDesign(designs, cat) {
  if (!designs || typeof designs !== 'object') return {};
  if (cat && designs[cat]) return designs[cat];
  const keys = Object.keys(designs || {});
  return keys.length ? designs[keys[0]] : {};
}

// Professional Booker Detail Modal
function BookerDetailModal({ open, onClose, booker }) {
  if (!booker) return null;
  const details = booker.bookingDetails || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5fa', color: (theme) => theme.palette.primary.main, fontWeight: 700, fontSize: 20, textAlign: 'center' }}>Booker Details</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.default : '#fafbff', p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: (theme) => theme.palette.primary.main, fontSize: 18 }}>
            {details.bookerName || details.companyDetails?.name || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, fontWeight: 500, fontSize: 15 }}>
            <b>Email:</b> {booker.userId?.email || details.email || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 500, fontSize: 15 }}>
            <b>Package:</b> {booker.packageId?.name || details.packageName || 'N/A'} | <b>Category:</b> {booker.packageId?.category || details.category || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#388e3c', fontSize: 15 }}>Status:</Typography>
            {booker.status === 'pending' ? (
              <span title="Pending" style={{ color: '#d32f2f', fontSize: 18 }}>✔</span>
            ) : booker.status === 'approved' ? (
              <span title="Approved" style={{ color: '#388e3c', fontSize: 18 }}>✔✔</span>
            ) : (
              <span style={{ fontWeight: 400 }}>{booker.status}</span>
            )}
            <Typography variant="body2" sx={{ fontWeight: 700, color: (theme) => theme.palette.primary.main, fontSize: 15, ml: 2 }}>Payment:</Typography>
            {booker.paymentStatus === 'pending' ? (
              <span title="Not Paid" style={{ color: '#d32f2f', fontSize: 18 }}>$</span>
            ) : booker.paymentStatus === 'paid' ? (
              <span title="Paid" style={{ color: '#388e3c', fontSize: 18 }}>$</span>
            ) : (
              <span style={{ fontWeight: 400 }}>{booker.paymentStatus}</span>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2, bgcolor: (theme) => theme.palette.divider }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Company:</b> {details.companyName || details.companyDetails?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Mobile:</b> {details.mobile || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {details.country && (
                <span title={details.country} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ReactCountryFlag
                    countryCode={getCountryCode(details.country)}
                    svg
                    style={{ width: '2.5em', height: '2.5em', borderRadius: '5px', boxShadow: '0 0 4px #007bff', border: '2px solid #007bff', verticalAlign: 'middle', marginRight: 8 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#007bff', fontSize: 16 }}>{details.country}</Typography>
                </span>
              )}
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}><b>Country:</b> {details.country || 'N/A'}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>City:</b> {details.city || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}><b>Address:</b> {details.address || details.companyDetails?.address || 'N/A'}</Typography>
              <Tooltip title="Location">
                <IconButton size="small" sx={{ color: (theme) => theme.palette.primary.main, p: 0.5 }}>
                  <span role="img" aria-label="location" style={{ fontSize: 18 }}>📍</span>
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Title:</b> {details.title || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Accommodation:</b> {details.accommodation ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Airport Pickup:</b> {details.airportPickup ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Date:</b> {booker.createdAt ? new Date(booker.createdAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Ticket Action Modal (Send/Print)
function TicketActionModal({ open, onClose, booker, ticketDesigns, onCreated, onNotify, onPreview }) {
  if (!booker) return null;
  // Determine design for booker's category
  const cat = booker.packageId?.category || 'attendee';
  // Always use latest design from ticketDesigns
  const design = pickDesign(ticketDesigns, cat);
  // Compose ticket data for preview
  const ticket = {
    type: cat,
    attendee: booker.bookingDetails?.bookerName || booker.bookingDetails?.companyDetails?.name || '',
    reference: booker._id,
    status: booker.status,
    company: booker.bookingDetails?.companyDetails?.name || '',
    email: booker.userId?.email || booker.bookingDetails?.email || '',
    phone: booker.bookingDetails?.phone || '',
    packageName: booker.packageId?.name || '',
    peopleCount: booker.bookingDetails?.peopleCount,
  };
  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Ticket Actions</DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {ticket.attendee}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Email: {ticket.email}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" gutterBottom>
          <b>Package:</b> {ticket.packageName} | <b>Category:</b> {cat}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <b>Status:</b> {ticket.status} | <b>Payment:</b> {booker.paymentStatus}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          {booker.ticketId ? (
            // Already has a ticket: show send and print
            <>
              <Button variant="contained" color="primary" startIcon={<SendOutlinedIcon />} onClick={async () => {
                try {
                  const res = await api.post(`/api/tickets/${booker.ticketId}/send`);
                  if (res.data && res.data.success) {
                    onNotify && onNotify('Ticket sent successfully', 'success');
                  } else {
                    onNotify && onNotify('Ticket send response: ' + JSON.stringify(res.data), 'info');
                  }
                } catch (err) {
                  onNotify && onNotify('Failed to send ticket: ' + (err.response?.data?.error || err.message), 'error');
                }
              }}>Send Ticket</Button>
              <Button variant="outlined" color="secondary" startIcon={<LocalPrintshopOutlinedIcon />} onClick={() => {
                // Open the print preview modal first (caller handles showing preview)
                const cat = booker.packageId?.category || '';
                const design = pickDesign(ticketDesigns, cat);
                const ticketData = { _id: booker.ticketId, bookingDetails: booker, type: cat, code: booker.ticketReference || booker.ticketId };
                if (onPreview) onPreview(design, ticketData);
              }}>Print</Button>
            </>
          ) : (
            // No ticket yet: allow create
            <Button variant="contained" color="success" onClick={async () => {
              try {
                const res = await api.post('/api/tickets', { bookingId: booker._id });
                if (res.data) {
                  onCreated && onCreated(res.data);
                    onNotify && onNotify('Ticket created', 'success');
                    // If caller provided onPreview, show preview for the newly created ticket before printing
                    const created = res.data.ticket || res.data;
                    const cat = booker.packageId?.category || '';
                    const design = pickDesign(ticketDesigns, cat);
                    const ticketData = { ...created, bookingDetails: booker };
                    if (onPreview) onPreview(design, ticketData);
                    onClose && onClose();
                }
              } catch (err) {
                onNotify && onNotify('Failed to create ticket: ' + (err.response?.data?.error || err.message), 'error');
              }
            }}>Create Ticket</Button>
          )}
        </Box>
        {/* Print preview area below */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Print Preview</Typography>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <TicketPreview design={design} ticket={ticket} />
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

import NotificationSnackbar from '../../components/NotificationSnackbar';

function TicketManagement() {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ticket: null });
  const userRole = getUserRole();
    // Detail and action modal state
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailBooker, setDetailBooker] = useState(null);
    const [actionOpen, setActionOpen] = useState(false);
    const [actionBooker, setActionBooker] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [eligibleBookers, setEligibleBookers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ code: '', bookingId: '', status: '', date: '' });
  const [preview, setPreview] = useState(null);
  const [designOpen, setDesignOpen] = useState(false);
  const [designType, setDesignType] = useState('attendee');
  const [packageCategories, setPackageCategories] = useState([]);
  // Ticket designs state
  const [ticketDesigns, setTicketDesigns] = useState({});
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewData, setPrintPreviewData] = useState({ design: null, ticket: null });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/tickets'),
      api.get('/api/bookings/eligible-bookers')
    ])
      .then(([ticketsRes, bookersRes]) => {
        setTickets(ticketsRes.data);
        setEligibleBookers(bookersRes.data);
      })
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch data', severity: 'error' }))
      .finally(() => setLoading(false));
    // Always load ticket designs from backend
    api.get('/api/ticket-designs').then(res => {
      if (res.data && typeof res.data === 'object') {
        setTicketDesigns(res.data);
      }
    }).catch(() => setTicketDesigns({}));
    // Get real package categories
    api.get('/api/packages').then(res => {
      let cats = [];
      if (Array.isArray(res.data.packages)) {
        cats = [...new Set(res.data.packages.map(p => p.category))];
      } else if (Array.isArray(res.data)) {
        cats = [...new Set(res.data.map(p => p.category))];
      }
      setPackageCategories(cats);
      if (cats.length && !cats.includes(designType)) setDesignType(cats[0]);
    }).catch(() => setPackageCategories([]));
  }, []);

  // Ensure ticket designs are loaded when opening Add Ticket dialog
  useEffect(() => {
    if (!addOpen) return;
    if (!ticketDesigns || Object.keys(ticketDesigns).length === 0) {
      api.get('/api/ticket-designs').then(res => {
        if (res.data && typeof res.data === 'object') setTicketDesigns(res.data);
      }).catch(() => {});
    }
  }, [addOpen]);

  const handleAddOpen = () => setAddOpen(true);
  const handleAddClose = () => { setAddOpen(false); setAddForm({ code: '', bookingId: '', status: '', date: '' }); setPreview(null); };
  const handleAddFormChange = (e) => { const { name, value } = e.target; setAddForm(f => ({ ...f, [name]: value })); };
  const handleAddSubmit = async () => {
    if (!addForm.bookingId) {
      setSnackbar({ open: true, message: 'Please select a booking', severity: 'error' });
      return;
    }
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      const payload = { bookingId: addForm.bookingId, code: addForm.code, date: addForm.date };
      const res = await api.post('/api/tickets', payload);
      const created = res.data && res.data.ticket ? res.data.ticket : res.data;
      const updatedBooking = res.data && res.data.booking ? res.data.booking : null;
      if (created) setTickets(prev => [...prev, created]);
      setSnackbar({ open: true, message: 'Ticket created successfully', severity: 'success' });
      if (updatedBooking) {
        setEligibleBookers(prev => prev.map(b => (String(b._id) === String(updatedBooking._id) ? { ...b, ...updatedBooking } : b)));
      }
      handleAddClose();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Create ticket failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  const handlePreview = () => setPreview({ ...addForm });

  const handleDesignSave = async (resData) => {
    // If child returned a single category save: { category, design }
    if (resData && resData.category && resData.design) {
      setTicketDesigns(prev => ({ ...prev, [resData.category]: resData.design }));
      setSnackbar({ open: true, message: `Design for ${resData.category} saved!`, severity: 'success' });
      setDesignOpen(false);
      return;
    }
    // Otherwise, attempt to accept a full designs object or refetch
    setSnackbar({ open: true, message: `Designs saved!`, severity: 'success' });
    setDesignOpen(false);
    try {
      const res = await api.get('/api/ticket-designs');
      if (res.data && typeof res.data === 'object') {
        setTicketDesigns(res.data);
      } else if (resData && typeof resData === 'object') {
        setTicketDesigns(resData);
      }
    } catch {
      if (resData && typeof resData === 'object') setTicketDesigns(resData);
    }
  };

  const handleTicketCreated = (data) => {
    const created = data && data.ticket ? data.ticket : null;
    const updatedBooking = data && data.booking ? data.booking : null;
    if (created) setTickets(prev => [...prev, created]);
    if (updatedBooking) {
      setEligibleBookers(prev => prev.map(b => (String(b._id) === String(updatedBooking._id) ? { ...b, ...updatedBooking } : b)));
      setSnackbar({ open: true, message: 'Ticket created for booking', severity: 'success' });
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Ticket Management
        </Typography>
        <Button variant="contained" color="primary" sx={{ mb: 2, mr: 2 }} onClick={handleAddOpen}>Add Ticket</Button>
        <Button variant="outlined" color="secondary" sx={{ mb: 2 }} onClick={() => setDesignOpen(true)}>Manage Ticket Designs</Button>
        <TicketDesignModal open={designOpen} onClose={() => setDesignOpen(false)} onSave={handleDesignSave} type={designType} />
        <NotificationSnackbar
          open={snackbar.open}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Eligible Bookers (Approved & Paid, including Speakers)</Typography>
        {loading ? <Typography>Loading...</Typography> : (
          <Grid container spacing={3}>
            {eligibleBookers.length === 0 ? (
              <Grid item xs={12}><Typography>No eligible bookers found.</Typography></Grid>
            ) : eligibleBookers.map((b) => {
                // Status icons
                let statusIcon = <HourglassEmptyOutlinedIcon color="warning" fontSize="small" titleAccess="Pending" />;
                if (b.status === 'approved' && b.paymentStatus === 'paid') {
                  statusIcon = <CheckCircleOutlineIcon color="success" fontSize="small" titleAccess="Approved & Paid" />;
                } else if (b.status === 'rejected' || b.paymentStatus === 'rejected') {
                  statusIcon = <CancelOutlinedIcon color="error" fontSize="small" titleAccess="Rejected" />;
                }
                // Ticket created marker (green ticket) if booking has ticketStatus
                if (b.ticketStatus === 'created' || b.ticketStatus === 'ticketed' || b.ticketStatus === 'issued') {
                  statusIcon = <ConfirmationNumberIcon color="success" fontSize="small" titleAccess="Ticket Created" />;
                }
                return (
                  <Grid item xs={12} sm={6} md={4} key={b._id}>
                    <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 4, mb: 2, position: 'relative', minHeight: 170 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', minHeight: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary.main" noWrap sx={{ fontSize: 15 }}>
                              {b.bookingDetails?.bookerName || b.bookingDetails?.companyDetails?.name || 'N/A'}
                            </Typography>
                          {b.bookingDetails?.country && (
                            <span title={b.bookingDetails.country} style={{ fontSize: 15 }}>
                              <ReactCountryFlag countryCode={getCountryCode(b.bookingDetails.country)} svg style={{ width: '1.1em', height: '1.1em', borderRadius: '2px', boxShadow: '0 0 2px #aaa' }} />
                            </span>
                          )}
                          {b.status === 'pending' ? (
                            <span title="Pending" style={{ color: '#d32f2f', fontSize: 16, marginLeft: 2 }}>✔</span>
                          ) : b.status === 'approved' ? (
                            <span title="Approved" style={{ color: '#388e3c', fontSize: 16, marginLeft: 2 }}>✔✔</span>
                          ) : (
                            <span style={{ fontWeight: 400, marginLeft: 2 }}>{b.status}</span>
                          )}
                          {b.paymentStatus === 'pending' ? (
                            <span title="Not Paid" style={{ color: '#d32f2f', fontSize: 16, marginLeft: 2 }}>$</span>
                          ) : b.paymentStatus === 'paid' ? (
                            <span title="Paid" style={{ color: '#388e3c', fontSize: 16, marginLeft: 2 }}>$</span>
                          ) : (
                            <span style={{ fontWeight: 400, marginLeft: 2 }}>{b.paymentStatus}</span>
                          )}
                          </Box>
                          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Status icon (approved/paid/rejected/ticketed) */}
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>{statusIcon}</span>
                            {/* If booking has ticketStatus show Ticket Actions button */}
                            {(b.ticketStatus === 'created' || b.ticketStatus === 'ticketed') && (
                              <Tooltip title="Ticket Actions">
                                <IconButton size="small" color="success" onClick={() => { setActionBooker(b); setActionOpen(true); }}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 13 }}>
                          <b>Email:</b> {b.userId?.email || b.bookingDetails?.email || 'N/A'}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                          <b>Package:</b> {b.packageId?.name || b.bookingDetails?.packageName || 'N/A'} | <b>Category:</b> {b.packageId?.category || b.bookingDetails?.category || 'N/A'}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                          <b>Company:</b> {b.bookingDetails?.companyName || b.bookingDetails?.companyDetails?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
                          <b>Mobile:</b> {b.bookingDetails?.mobile || 'N/A'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                          <Tooltip title="Create Ticket">
                            <IconButton size="small" color="primary" onClick={() => { setAddForm(f => ({ ...f, bookingId: b._id })); setAddOpen(true); }}>
                              <LocalPrintshopOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Resend">
                            <IconButton size="small" color="secondary">
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Scan Logs">
                            <IconButton size="small">
                              <LocalPrintshopOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => { setDetailBooker(b); setDetailOpen(true); }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
          </Grid>
        )}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Ticketed Bookers</Typography>
          <Grid container spacing={3}>
            {tickets.length === 0 ? (
              <Grid item xs={12}><Typography>No ticketed bookers found.</Typography></Grid>
            ) : tickets.map((ticket) => {
              const booking = ticket.bookingDetails || ticket.booking || {};
              const category = ticket.type || booking.packageId?.category || booking.bookingDetails?.category || '';
              const design = pickDesign(ticketDesigns, category);
              return (
                <Grid item xs={12} sm={6} md={4} key={ticket._id}>
                  <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 4, mb: 2, position: 'relative' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {booking?.bookerName || booking?.companyDetails?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Ticket Code:</b> {ticket.code || ticket.reference || ticket._id}
                    </Typography>
                    <Typography variant="body2">
                      <b>Status:</b> {ticket.status}
                    </Typography>
                    <Typography variant="body2">
                      <b>Date:</b> {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => {
                        setPrintPreviewData({ design, ticket: { ...ticket, bookingDetails: booking } });
                        setPrintPreviewOpen(true);
                      }} startIcon={<LocalPrintshopOutlinedIcon />}>Preview</Button>
                      <Button size="small" variant="contained" color="primary" startIcon={<SendOutlinedIcon />} onClick={async () => {
                        try {
                          const res = await api.post(`/api/tickets/${ticket._id}/send`);
                          if (res.data && res.data.success) {
                            setSnackbar({ open: true, message: 'Ticket sent', severity: 'success' });
                          } else {
                            setSnackbar({ open: true, message: 'Send response: ' + JSON.stringify(res.data), severity: 'info' });
                          }
                        } catch (err) {
                          setSnackbar({ open: true, message: 'Failed to send: ' + (err.response?.data?.error || err.message), severity: 'error' });
                        }
                      }}>Send</Button>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<LocalPrintshopOutlinedIcon />} onClick={() => {
                        setPrintPreviewData({ design, ticket: { ...ticket, bookingDetails: booking } });
                        setPrintPreviewOpen(true);
                      }}>Print</Button>
                      {userRole === 'superadmin' && (
                        <Tooltip title="Delete Ticket">
                          <IconButton size="small" color="error" sx={{ ml: 1, border: '1px solid #e57373', background: '#fff', '&:hover': { background: '#ffeaea' } }} onClick={() => setDeleteDialog({ open: true, ticket })}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
                {/* Delete Ticket Confirmation Dialog */}
                <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, ticket: null })}>
                  <DialogTitle>Delete Ticket</DialogTitle>
                  <DialogContent>
                    <Typography>Are you sure you want to delete this ticket? This action cannot be undone.</Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, ticket: null })}>Cancel</Button>
                    <Button color="error" variant="contained"
                      onClick={async () => {
                        if (!deleteDialog.ticket) return;
                        try {
                          await api.delete(`/api/tickets/${deleteDialog.ticket._id}`);
                          setTickets(tickets => tickets.filter(t => t._id !== deleteDialog.ticket._id));
                          setSnackbar({ open: true, message: 'Ticket deleted successfully', severity: 'success' });
                        } catch (err) {
                          setSnackbar({ open: true, message: 'Failed to delete ticket: ' + (err.response?.data?.error || err.message), severity: 'error' });
                        }
                        setDeleteDialog({ open: false, ticket: null });
                      }}
                    >Delete</Button>
                  </DialogActions>
                </Dialog>
          </Grid>
          {/* Booker Detail Modal */}
          <BookerDetailModal open={detailOpen} onClose={() => setDetailOpen(false)} booker={detailBooker} />
          {/* Ticket Action Modal */}
          <TicketActionModal 
            open={!!actionOpen} 
            onClose={() => setActionOpen(false)} 
            booker={actionBooker} 
            ticketDesigns={ticketDesigns} 
            onCreated={handleTicketCreated}
            onNotify={(message, severity='info') => setSnackbar({ open: true, message, severity })}
            onPreview={(design, ticket) => { setPrintPreviewData({ design, ticket }); setPrintPreviewOpen(true); }}
          />
          <PrintPreviewModal 
            open={printPreviewOpen} 
            onClose={() => setPrintPreviewOpen(false)} 
            design={printPreviewData.design} 
            ticket={printPreviewData.ticket} 
          />
        {/* Existing tickets table can be shown below or in a tab */}
        <Dialog open={addOpen} onClose={handleAddClose} maxWidth="md" fullWidth>
          <DialogTitle>Add Ticket</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  autoFocus
                  margin="dense"
                  name="code"
                  label="Ticket Code"
                  fullWidth
                  value={addForm.code}
                  onChange={handleAddFormChange}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth margin="dense">
                  <InputLabel id="add-booking-label">Booking (Eligible Booker)</InputLabel>
                  <Select
                    labelId="add-booking-label"
                    label="Booking (Eligible Booker)"
                    name="bookingId"
                    value={addForm.bookingId}
                    onChange={handleAddFormChange}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="">Select Eligible Booker</MenuItem>
                    {eligibleBookers.map(b => (
                      <MenuItem key={b._id} value={b._id}>
                        {b.bookingDetails?.bookerName || b.bookingDetails?.companyDetails?.name || b.userId?.email || b._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  margin="dense"
                  name="status"
                  label="Status"
                  fullWidth
                  value={addForm.status}
                  onChange={handleAddFormChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  margin="dense"
                  name="date"
                  label="Date"
                  type="date"
                  fullWidth
                  value={addForm.date}
                  onChange={handleAddFormChange}
                  InputLabelProps={{ shrink: true }}
                />
                <Button onClick={handlePreview} sx={{ mt: 2 }}>Preview</Button>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 360 }, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                {addForm.bookingId ? (
                  (() => {
                    const selectedBooking = eligibleBookers.find(b => String(b._id) === String(addForm.bookingId));
                    const category = selectedBooking?.packageId?.category || selectedBooking?.bookingDetails?.category || '';
                    const design = pickDesign(ticketDesigns, category);
                    const ticketForPreview = {
                      type: category,
                      attendee: selectedBooking?.bookingDetails?.bookerName || selectedBooking?.bookingDetails?.companyDetails?.name || '',
                      reference: addForm.code || selectedBooking?._id,
                      status: addForm.status || selectedBooking?.status || '',
                      company: selectedBooking?.bookingDetails?.companyDetails?.name || '',
                      email: selectedBooking?.userId?.email || selectedBooking?.bookingDetails?.email || '',
                      phone: selectedBooking?.bookingDetails?.mobile || selectedBooking?.bookingDetails?.phone || '',
                      packageName: selectedBooking?.packageId?.name || selectedBooking?.bookingDetails?.packageName || '',
                      peopleCount: selectedBooking?.bookingDetails?.peopleCount
                    };
                    return (
                      <TicketPreview design={design} ticket={ticketForPreview} />
                    );
                  })()
                ) : preview && (
                  <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, width: '100%' }}>
                    <Typography variant="h6">Ticket Preview</Typography>
                    <Typography>Code: {preview.code}</Typography>
                    <Typography>Booker: {eligibleBookers.find(b => b._id === addForm.bookingId)?.bookingDetails?.bookerName || addForm.bookingId}</Typography>
                    <Typography>Status: {preview.status}</Typography>
                    <Typography>Date: {preview.date}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose}>Cancel</Button>
            <Button onClick={handleAddSubmit} variant="contained">Create Ticket</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default TicketManagement;
